import {
  IconAlertTriangle,
  IconBan,
  IconDatabase,
  IconFlag,
  IconWand,
} from '@tabler/icons-react';

import {
  ScopeMode,
  AssertionField,
  AssertionOperator,
  ServiceStartDayValue,
  ApplyField,
  PatientAgeOperator,
  WhenLogic,
  RuleMode,
  RuleSeverity,
} from '@billing/validation/rules';

import type { RuleCriteriaGroup as WhenGroup } from '@billing/validation/rules';
import type { AssertionRow, WhenClause, WhenOperator } from './ruleSchemas';
import type { ClaimFieldOption } from 'constants/tariffConstants';

/** Inputs shared by summary formatters without depending on the builder hook or rendering components. */
export interface RuleSummaryContext {
  translate: RuleTranslate;
  timezone: string;
  getDemographicOptions: (scopeMode: ScopeMode) => ClaimFieldOption[];
  getAssertionDemographicOptions: (field: AssertionField) => ClaimFieldOption[];
  severity: RuleSeverity;
  assertions: AssertionRow[];
  exactTariffValues: string[];
  whenLogic: WhenLogic;
  whenClauses: WhenClause[];
  whenGroups: WhenGroup[];
}

export interface RuleSummaryFormatters {
  formatWhenClause: (clause: WhenClause) => string;
  formatValidationSummary: () => string[];
  localizedApplyFieldLabel: (field: string) => string;
}

/** Criteria groups carry their own connector so saved checks retain their original meaning. */
export interface RuleCriteriaGroup {
  key: 'criteria' | 'savedChecks';
  logic: WhenLogic;
  headingKey?: string;
  rows: { clause: WhenClause; index: number; joinerKey?: string }[];
}

/** Maps older outcome checks to the same criterion definitions used by both flows. */
export const historyCriteriaFields = [
  {
    scopeMode: ScopeMode.DuplicateClaim,
    assertionField: AssertionField.DuplicateClaim,
    operator: 'exists',
  },
  {
    scopeMode: ScopeMode.TariffBillingHistory,
    assertionField: AssertionField.TariffBillingHistory,
    operator: 'is',
  },
] as const;

export const historyCriteriaScopeModes: ReadonlySet<ScopeMode> = new Set(
  historyCriteriaFields.map((field) => field.scopeMode)
);

export const billingHistoryPeriodOptions = [
  { value: 'calendarDay', labelKey: 'sameDay' },
  { value: 'calendarWeek', labelKey: 'sameWeek' },
  { value: 'last30Days', labelKey: 'last30Days' },
  { value: 'calendarYear', labelKey: 'sameYear' },
  { value: 'anyTime', labelKey: 'anyTime' },
] as const;

export type ClaimCriteriaInput =
  | 'select'
  | 'identifier'
  | 'number'
  | 'text'
  | 'date';

export interface ClaimCriteriaField {
  /** Saved criteria identifier; kept stable when reopening existing rules. */
  scopeMode: ScopeMode;
  /** Existing rule-engine fact identifier shared with validation checks. */
  fact: AssertionField;
  /** Chooses the value control, supported operators, and comparison semantics. */
  input: ClaimCriteriaInput;
}

/** Defines each claim criterion once for the picker, controls, summaries, and compiler. */
export const claimCriteriaFields: readonly ClaimCriteriaField[] = [
  {
    scopeMode: ScopeMode.LocationOfService,
    fact: AssertionField.ClaimLocationOfService,
    input: 'select',
  },
  {
    scopeMode: ScopeMode.ServiceLocationIndicator,
    fact: AssertionField.ClaimServiceLocationIndicator,
    input: 'select',
  },
  {
    scopeMode: ScopeMode.ReferringProvider,
    fact: AssertionField.ReferringProvider,
    input: 'identifier',
  },
  {
    scopeMode: ScopeMode.HospitalCode,
    fact: AssertionField.ClaimHospitalCode,
    input: 'select',
  },
  {
    scopeMode: ScopeMode.FacilityNumber,
    fact: AssertionField.FacilityNumber,
    input: 'identifier',
  },
  {
    scopeMode: ScopeMode.PatientCount,
    fact: AssertionField.PatientCount,
    input: 'number',
  },
  {
    scopeMode: ScopeMode.VirtualPhysicianRegion,
    fact: AssertionField.VirtualPhysicianRegion,
    input: 'select',
  },
  {
    scopeMode: ScopeMode.ProviderComments,
    fact: AssertionField.ProviderComments,
    input: 'text',
  },
  {
    scopeMode: ScopeMode.ClerkComments,
    fact: AssertionField.ClaimClerkComments,
    input: 'text',
  },
  {
    scopeMode: ScopeMode.InternalComments,
    fact: AssertionField.InternalComments,
    input: 'text',
  },
  {
    scopeMode: ScopeMode.ProviderNotes,
    fact: AssertionField.ProviderNotes,
    input: 'text',
  },
  {
    scopeMode: ScopeMode.ServiceStart,
    fact: AssertionField.ServiceStart,
    input: 'date',
  },
  {
    scopeMode: ScopeMode.ServiceEnd,
    fact: AssertionField.ServiceEnd,
    input: 'date',
  },
  {
    scopeMode: ScopeMode.InterpretingRadiologist,
    fact: AssertionField.InterpretingRadiologist,
    input: 'identifier',
  },
  {
    scopeMode: ScopeMode.OptometristReason,
    fact: AssertionField.OptometristReason,
    input: 'select',
  },
  {
    scopeMode: ScopeMode.ThirdPartyLiability,
    fact: AssertionField.ThirdPartyLiability,
    input: 'select',
  },
];

const valueOperators: WhenOperator[] = ['is', 'isNot'];
const existenceOperators: WhenOperator[] = ['exists', 'notExists'];
export const whenRangeOperators: WhenOperator[] = ['between', 'notBetween'];
export const claimCriteriaOperatorsByInput: Record<
  ClaimCriteriaInput,
  WhenOperator[]
> = {
  select: [...valueOperators, ...existenceOperators],
  identifier: [...valueOperators, ...existenceOperators],
  number: [
    ...valueOperators,
    'lessThan',
    'lessThanInclusive',
    'greaterThan',
    'greaterThanInclusive',
    ...whenRangeOperators,
    ...existenceOperators,
  ],
  date: [
    ...valueOperators,
    'onOrAfter',
    'onOrBefore',
    ...whenRangeOperators,
    ...existenceOperators,
  ],
  text: [...valueOperators, 'contains', 'notContains', ...existenceOperators],
};

export const RULE_MESSAGE_MAX_LENGTH = 250;

export type RuleTranslate = (key: string) => string;

export const supportedScopeModes: ScopeMode[] = [
  ScopeMode.AnyClaim,
  ...historyCriteriaScopeModes,
  ...claimCriteriaFields.map((field) => field.scopeMode),
  ScopeMode.PatientAge,
  ScopeMode.PatientProvince,
  ScopeMode.PatientSex,
  ScopeMode.PatientMaritalStatus,
  ScopeMode.ServiceStartTimeLocal,
  ScopeMode.ServiceStartDayLocal,
  ScopeMode.ExactTariff,
  ScopeMode.TariffNotEqual,
  ScopeMode.TariffCode,
  ScopeMode.TariffPrefix,
  ScopeMode.SpecialtyCode,
  ScopeMode.TariffHasAnestheticUnitValue,
];

export const selectableScopeModes: ScopeMode[] = supportedScopeModes.filter(
  (scopeMode) => scopeMode !== ScopeMode.TariffNotEqual
);

export const tariffScopeModes = new Set<ScopeMode>([
  ScopeMode.ExactTariff,
  ScopeMode.TariffNotEqual,
  ScopeMode.TariffCode,
  ScopeMode.TariffPrefix,
  ScopeMode.SpecialtyCode,
]);

export const categoricalDemographicScopeModes = new Set<ScopeMode>([
  ScopeMode.PatientProvince,
  ScopeMode.PatientSex,
  ScopeMode.PatientMaritalStatus,
]);

export const demographicFactByScopeMode: Partial<Record<ScopeMode, string>> = {
  [ScopeMode.PatientAge]: 'patientAgeAtServiceStart',
  [ScopeMode.PatientProvince]: 'patientProvince',
  [ScopeMode.PatientSex]: 'patientSex',
  [ScopeMode.PatientMaritalStatus]: 'patientMaritalStatus',
};

export const assertionFields: AssertionField[] = [
  AssertionField.DuplicateClaim,
  AssertionField.TariffBillingHistory,
  AssertionField.ReferringProvider,
  AssertionField.ProviderComments,
  AssertionField.ClaimClerkComments,
  AssertionField.InternalComments,
  AssertionField.ProviderNotes,
  AssertionField.ClaimServiceLocationIndicator,
  AssertionField.ClaimLocationOfService,
  AssertionField.ClaimPatientName,
  AssertionField.PatientAge,
  AssertionField.PatientProvince,
  AssertionField.PatientSex,
  AssertionField.PatientMaritalStatus,
  AssertionField.ClaimHospitalCode,
  AssertionField.HospitalName,
  AssertionField.FacilityNumber,
  AssertionField.InterpretingRadiologist,
  AssertionField.VirtualPhysicianRegion,
  AssertionField.OptometristReason,
  AssertionField.ThirdPartyLiability,
  AssertionField.Incorporated,
  AssertionField.ConfidentialCode,
  AssertionField.ChiroService,
  AssertionField.PatientCount,
  AssertionField.ServiceStart,
  AssertionField.ServiceEnd,
  AssertionField.ClaimServiceStartMinutesLocal,
  AssertionField.ClaimServiceStartDayOfWeekLocal,
];

export const assertionOperators: AssertionOperator[] = [
  AssertionOperator.Equals,
  AssertionOperator.NotEquals,
  AssertionOperator.Contains,
  AssertionOperator.LessThan,
  AssertionOperator.LessThanInclusive,
  AssertionOperator.GreaterThan,
  AssertionOperator.GreaterThanInclusive,
  AssertionOperator.In,
  AssertionOperator.NotIn,
  AssertionOperator.MustExist,
  AssertionOperator.BetweenInclusive,
  AssertionOperator.DateOnOrAfter,
  AssertionOperator.DateOnOrBefore,
];

/** Localized phrases used when describing saved validation requirements. */
export const assertionSummaryOperatorKeys: Partial<
  Record<AssertionOperator, string>
> = {
  [AssertionOperator.Equals]: 'pages:rules.editControls.mustBe',
  [AssertionOperator.NotEquals]: 'pages:rules.editControls.mustNotBe',
  [AssertionOperator.Contains]: 'pages:rules.editControls.mustContain',
  [AssertionOperator.In]: 'pages:rules.editControls.mustBeOneOf',
  [AssertionOperator.NotIn]: 'pages:rules.editControls.mustNotBeOneOf',
  [AssertionOperator.BetweenInclusive]:
    'pages:rules.editControls.mustBeBetween',
  [AssertionOperator.NotBetweenInclusive]:
    'pages:rules.editControls.mustNotBeBetween',
};

export const timeAssertionOperators: AssertionOperator[] = [
  AssertionOperator.Equals,
  AssertionOperator.BetweenInclusive,
  AssertionOperator.NotBetweenInclusive,
];

export const dateAssertionOperators: AssertionOperator[] = [
  AssertionOperator.Equals,
  AssertionOperator.NotEquals,
  AssertionOperator.BetweenInclusive,
  AssertionOperator.DateOnOrAfter,
  AssertionOperator.DateOnOrBefore,
  AssertionOperator.MustExist,
];

export const defaultAssertionOperators: AssertionOperator[] = [
  AssertionOperator.Equals,
  AssertionOperator.NotEquals,
  AssertionOperator.In,
  AssertionOperator.NotIn,
  AssertionOperator.MustExist,
];

export const stringContainsAssertionFields = new Set<AssertionField>([
  AssertionField.ProviderComments,
  AssertionField.ClaimClerkComments,
  AssertionField.InternalComments,
]);

export const stringAssertionOperators: AssertionOperator[] = [
  AssertionOperator.Equals,
  AssertionOperator.NotEquals,
  AssertionOperator.Contains,
  AssertionOperator.In,
  AssertionOperator.NotIn,
  AssertionOperator.MustExist,
];

export const ageAssertionOperators: AssertionOperator[] = [
  AssertionOperator.Equals,
  AssertionOperator.NotEquals,
  AssertionOperator.LessThan,
  AssertionOperator.LessThanInclusive,
  AssertionOperator.GreaterThan,
  AssertionOperator.GreaterThanInclusive,
  AssertionOperator.BetweenInclusive,
  AssertionOperator.NotBetweenInclusive,
  AssertionOperator.MustExist,
];

export const categoricalDemographicAssertionOperators: AssertionOperator[] = [
  AssertionOperator.Equals,
  AssertionOperator.NotEquals,
  AssertionOperator.In,
  AssertionOperator.NotIn,
  AssertionOperator.MustExist,
];

export const timeBasedApplyFields = new Set<string>([
  ApplyField.TimeBasedServiceUnits,
  ApplyField.TimeBasedAnesthesiaUnits,
]);

export const TIME_BASED_INTERVALS = ['15', '30', '45', '60'] as const;

export const timeBasedIntervalOptions: { value: string }[] = [
  { value: '15' },
  { value: '30' },
  { value: '45' },
  { value: '60' },
];

export const applyFieldOptions: { value: ApplyField }[] = [
  { value: ApplyField.ReferringProvider },
  { value: ApplyField.ProviderComments },
  { value: ApplyField.ClerkComments },
  { value: ApplyField.InternalComments },
  { value: ApplyField.ProviderNotes },
  {
    value: ApplyField.ServiceLocationIndicator,
  },
  { value: ApplyField.LocationOfService },
  { value: ApplyField.HospitalCode },
  { value: ApplyField.HospitalName },
  { value: ApplyField.FacilityNumber },
  {
    value: ApplyField.InterpretingRadiologist,
  },
  {
    value: ApplyField.VirtualPhysicianRegion,
  },
  { value: ApplyField.OptometristReason },
  { value: ApplyField.ThirdPartyLiability },
  { value: ApplyField.Incorporated },
  { value: ApplyField.ConfidentialCode },
  { value: ApplyField.ChiroService },
  { value: ApplyField.PatientCount },
  { value: ApplyField.ServiceStart },
  { value: ApplyField.ServiceEnd },
  { value: ApplyField.TariffCode },
  {
    value: ApplyField.TimeBasedServiceUnits,
  },
  {
    value: ApplyField.TimeBasedAnesthesiaUnits,
  },
];

export const dayOptions: { value: ServiceStartDayValue }[] = [
  { value: ServiceStartDayValue.Sunday },
  { value: ServiceStartDayValue.Monday },
  { value: ServiceStartDayValue.Tuesday },
  { value: ServiceStartDayValue.Wednesday },
  { value: ServiceStartDayValue.Thursday },
  { value: ServiceStartDayValue.Friday },
  { value: ServiceStartDayValue.Saturday },
];

export const whenOperatorOptions: { value: WhenOperator }[] = [
  { value: 'is' },
  { value: 'isNot' },
  { value: 'exists' },
  { value: 'notExists' },
];

export const patientAgeOperatorOptions: {
  value: PatientAgeOperator;
}[] = [
  { value: PatientAgeOperator.Equal },
  { value: PatientAgeOperator.LessThan },
  {
    value: PatientAgeOperator.LessThanInclusive,
  },
  { value: PatientAgeOperator.GreaterThan },
  {
    value: PatientAgeOperator.GreaterThanInclusive,
  },
  { value: PatientAgeOperator.BetweenInclusive },
];

export const patientSexOptions = [
  { value: 'M' },
  { value: 'F' },
  { value: 'X' },
];

export const patientMaritalStatusOptions = [
  { value: 'S' },
  { value: 'M' },
  { value: 'D' },
  { value: 'W' },
  { value: 'SEP' },
];

export const ruleActions = [
  {
    value: RuleSeverity.Block,
    color: 'red',
    Icon: IconBan,
    labelKey: 'actions.block.label',
    descriptionKey: 'actions.block.description',
    exampleKey: 'actions.block.example',
  },
  {
    value: RuleSeverity.Alert,
    color: 'yellow',
    Icon: IconAlertTriangle,
    labelKey: 'actions.alert.label',
    descriptionKey: 'actions.alert.description',
    exampleKey: 'actions.alert.example',
  },
  {
    value: RuleMode.ApplyData,
    color: 'teal',
    Icon: IconDatabase,
    labelKey: 'actions.applyData.label',
    descriptionKey: 'actions.applyData.description',
    exampleKey: 'actions.applyData.example',
  },
  {
    value: 'flagOnly',
    color: 'grape',
    Icon: IconFlag,
    labelKey: 'edit.flagOnly',
    descriptionKey: 'edit.flagDescription',
    exampleKey: 'edit.flagExample',
  },
  {
    value: RuleMode.TariffTransforms,
    color: 'blue',
    Icon: IconWand,
    labelKey: 'edit.transforms',
    descriptionKey: 'edit.transformsDescription',
    exampleKey: 'edit.transformsExample',
  },
] as const;

export type RuleEditorSection = 'action' | 'criteria' | 'outcome' | 'name';

export type RuleEditorSnapshot = Record<RuleEditorSection, unknown>;

export type RuleCondition = Record<string, unknown>;
